import { useTheme } from '~/common/theme'

import { Nullable } from 'shared/utils/type-guards'

import { RateTypeEnum, TaskStatusEnum } from '~/common/types/graphql'
import MultipleProgressBar from '~/views/components/ui/MultipleProgressBar'
import Text from '~/views/components/ui/Text'

import HourValueList from './HourValueList'

import { DataWrapper, LargeDataLabelWrapper } from './Styled'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

const progressBarValueFrom = ({
  percentage = 0,
  color = '',
  hoverText = '',
  status = '',
}) => ({
  percentage: percentage,
  color: color,
  hoverText: hoverText,
  status: status,
})

export interface Props {
  tasks: {
    overdue?: number
    remaining?: number
    done?: number
    planned?: number
    notStarted?: number
    signOff?: number
    stuck?: number
    working?: number
  }
  hours?: null | {
    estimated?: number
    actual?: number
    remaining?: number
    nonBillableHours?: number
    billableHours?: number
    loggedHours?: number
    remainingPurchasedHours?: number
  }
  project?: {
    id: string
    firstActiveTemplate?: string | null
    organization?: Nullable<{ timeTrackingEnabled?: Nullable<boolean> }>
    projectBillRate?: Nullable<{
      rateType?: Nullable<RateTypeEnum>
      hours?: Nullable<number>
    }>
  }
  isProvider?: boolean
}

const TaskHeroNumberV2 = (props: Props) => {
  const [getCommonLabel, getProjectOverviewLabel] = useStaticLabels([
    'common',
    'projectOverview',
  ])
  const { project, tasks, hours, isProvider = false } = props

  const theme = useTheme()

  const {
    done: doneTasks = 0,
    notStarted: notStartedTasks = 0,
    signOff: signOffTasks = 0,
    stuck: stuckTasks = 0,
    working: workingTasks = 0,
  } = tasks

  return (
    <DataWrapper data-testid={'details-container'}>
      <LargeDataLabelWrapper data-testid={'remaining-tasks-container'}>
        <Text size="mega" weight="bold" data-testid={'remaining-tasks'}>
          {tasks.remaining}
        </Text>
        <Text color="lightGray" data-testid={'remaining-tasks-label'}>
          {getProjectOverviewLabel('projectOverviewRemainingTasks')}
        </Text>
      </LargeDataLabelWrapper>

      <div data-testid={'bottom-content'}>
        <HourValueList
          project={project}
          hours={hours}
          isProvider={isProvider}
        />

        <div
          style={{
            minWidth: '160px',
            paddingTop: '5px',
          }}
          data-testid={'progress-bar-container'}
        >
          <MultipleProgressBar
            /* if useLink is false, the onClick function is not executed, and
            task filters are not present in router query when
            redirecting to Project "Plan" tab */
            useLink={true}
            onClick={(e) => e.preventDefault()}
            projectId={project?.id || ''}
            firstActiveTemplateId={project?.firstActiveTemplate}
            size="xxs"
            values={[
              progressBarValueFrom({
                percentage: doneTasks,
                color: theme.palette.status.done,
                hoverText: getCommonLabel('done'),
                status: TaskStatusEnum.Done,
              }),
              progressBarValueFrom({
                percentage: signOffTasks,
                color: theme.palette.status.signOff,
                hoverText: getCommonLabel('signOff'),
                status: TaskStatusEnum.SignOff,
              }),
              progressBarValueFrom({
                percentage: stuckTasks,
                color: theme.palette.status.stuck,
                hoverText: getCommonLabel('stuck'),
                status: TaskStatusEnum.Stuck,
              }),
              progressBarValueFrom({
                percentage: workingTasks,
                color: theme.palette.status.workingOnIt,
                hoverText: getCommonLabel('inProgress'),
                status: TaskStatusEnum.WorkingOnIt,
              }),
              progressBarValueFrom({
                percentage: notStartedTasks,
                color: theme.palette.status.notStarted,
                hoverText: getCommonLabel('notStarted'),
                status: TaskStatusEnum.NotStarted,
              }),
            ]}
          />
        </div>
      </div>
    </DataWrapper>
  )
}

export default TaskHeroNumberV2
